import type { Candidate, Configuration, Options, Pair, ResultRow, RowError, Rule } from './types';

export function parseSkuText(text: string): string[] {
  const values = text.trim().split(/[\r\n,;\t]+/).map(value => value.trim());
  return text.trim() ? values.filter(Boolean) : [];
}

export function makePairs(baseText: string, sourceText: string, configuration: Configuration): Pair[] {
  // Preserve interior empty lines in paired input: removing them independently
  // could silently shift the correspondence between two SKU lists.
  const lines = (text: string) => text.trim().split(/\r?\n|[,;\t]/).map(value => value.trim());
  const base = configuration.mode === 'skuPairs' ? lines(baseText) : parseSkuText(baseText);
  const source = configuration.mode === 'skuPairs' ? lines(sourceText) : [];
  if (!base.length || base.some(value => !value)) throw new Error('Ingresa SKUs base y elimina las filas vacías entre ellos.');
  if (configuration.mode === 'skuPairs' && (base.length !== source.length || source.some(value => !value))) {
    throw new Error('Las listas deben tener la misma cantidad de SKUs, sin filas vacías. Cada posición forma un par.');
  }
  const pairs = base.map((baseSku, index) => ({ baseSku, skuToIntegrate: configuration.mode === 'skuPairs'
    ? source[index] : configuration.mode === 'suffix' ? `${baseSku}${configuration.suffix}` : baseSku }));
  validatePairs(pairs);
  return pairs;
}

export function validatePairs(pairs: Pair[]) {
  if (!pairs.length) throw new Error('Ingresa al menos un SKU base.');
  if (pairs.length > 100) throw new Error('Puedes procesar hasta 100 filas por consolidación.');
  const keys = pairs.map(pair => JSON.stringify([pair.baseSku, pair.skuToIntegrate]));
  if (new Set(keys).size !== keys.length) throw new Error('Hay pares de SKUs repetidos. Elimina los duplicados antes de continuar.');
}

export function validateConfiguration(configuration: Configuration) {
  const locale = /^[A-Za-z][A-Za-z0-9_-]{1,15}$/;
  const forbidden = new Set(['__proto__', 'prototype', 'constructor']);
  if ([configuration.sourceLocale, configuration.targetLocale].some(value => !locale.test(value) || forbidden.has(value))) {
    throw new Error('Selecciona o ingresa códigos de localización válidos.');
  }
  if (configuration.mode === 'suffix' && (!configuration.suffix || configuration.suffix.trim() !== configuration.suffix)) {
    throw new Error('Ingresa un sufijo literal sin espacios en los extremos, por ejemplo -PE.');
  }
  if (configuration.mode === 'sameSku') {
    if (!configuration.baseRules.rules.length) throw new Error('Agrega al menos una condición para identificar el modelo base.');
    for (const rule of configuration.baseRules.rules) {
      if (!/^[A-Za-z_][A-Za-z0-9_]*(\.[A-Za-z_][A-Za-z0-9_]*)*$/.test(rule.path)
        || rule.path.split('.').some(key => forbidden.has(key))) throw new Error('Revisa las rutas de las condiciones, por ejemplo langs.esCL.description.');
      if (rule.operator === 'igual' && rule.value === undefined) throw new Error('Completa el valor de las condiciones de igualdad.');
      if (typeof rule.value === 'number' && !Number.isFinite(rule.value)) throw new Error('Ingresa un número válido en la condición.');
    }
  }
}

export function optionsOnly(configuration: Options): Options {
  return { sourceLocale: configuration.sourceLocale, targetLocale: configuration.targetLocale, overwrite: configuration.overwrite };
}

export function previewRequest(configuration: Configuration, pair: Pair) {
  const common = optionsOnly(configuration);
  if (configuration.mode === 'sameSku') return { ...common, mode: 'sameSku', skus: [pair.baseSku], baseRules: configuration.baseRules };
  if (configuration.mode === 'suffix') return { ...common, mode: 'suffix', skus: [pair.baseSku], suffix: configuration.suffix };
  return { ...common, mode: 'skuPairs', pairs: [pair] };
}

export function getRowError(error: unknown, executing = false): RowError {
  const candidate = error as { response?: { status?: number; data?: { error?: RowError } }; message?: string } | null;
  const upstream = candidate?.response?.data?.error;
  if (upstream) return { ...upstream, status: candidate?.response?.status };
  return {
    code: executing ? 'INTEGRATION_OUTCOME_UNKNOWN' : 'PREVIEW_REQUEST_FAILED',
    message: executing
      ? 'No se pudo confirmar el resultado. Consulta los modelos antes de volver a ejecutar esta fila.'
      : candidate?.message ?? 'No fue posible consultar este SKU.',
  };
}

function content(model: Candidate, locale: string): unknown {
  return model.langs && Object.prototype.hasOwnProperty.call(model.langs, locale) ? model.langs[locale] : undefined;
}

export function chooseModels(row: ResultRow, baseId: string, sourceId: string, options: Options): ResultRow {
  const base = row.candidates.base.find(model => model.id === baseId);
  const source = row.candidates.toIntegrate.find(model => model.id === sourceId);
  if (!base || !source) throw new Error('Selecciona un modelo de cada grupo.');
  if (base.id === source.id) throw new Error('El modelo base y el modelo a integrar deben ser distintos.');
  const localizedContent = content(source, options.sourceLocale);
  if (!localizedContent || typeof localizedContent !== 'object' || Array.isArray(localizedContent)) {
    throw new Error(`El modelo a integrar no tiene contenido en ${options.sourceLocale}.`);
  }
  const targetExists = content(base, options.targetLocale) !== undefined;
  if (targetExists && !options.overwrite) throw new Error(`El modelo base ya tiene ${options.targetLocale}. Activa sobrescritura y genera un nuevo dry-run si deseas reemplazarlo.`);
  return { ...row, status: 'ready', selected: true, error: undefined,
    selection: { baseSku: row.baseSku, skuToIntegrate: row.skuToIntegrate, modelBase: base.id, modelToIntegrate: source.id },
    changes: { sourcePath: `langs.${options.sourceLocale}`, targetPath: `langs.${options.targetLocale}`,
      localizedContent, targetExists, reassignIndexados: true, translated: true, integratedModelState: false } };
}

export function buildModelPreview(row: ResultRow, options: Options): unknown {
  const base = row.candidates.base.find(model => model.id === row.selection?.modelBase);
  return base && row.changes ? { ...base, langs: { ...base.langs, [options.targetLocale]: row.changes.localizedContent } } : null;
}

export function duplicatedModels(rows: ResultRow[]): string[] {
  const counts = new Map<string, number>();
  for (const row of rows) {
    if (!row.selection) continue;
    for (const id of new Set([row.selection.modelBase, row.selection.modelToIntegrate])) counts.set(id, (counts.get(id) ?? 0) + 1);
  }
  return [...counts].filter(([, count]) => count > 1).map(([id]) => id);
}

export function asRule(path: string, operator: Rule['operator'], value: Rule['value']): Rule {
  return operator === 'existe' || operator === 'noExiste' ? { path, operator } : { path, operator, value };
}
